import { useState } from 'react'
import { toast } from 'sonner'
import {
  PLANS,
  type BroadcastSegmentKey,
  type PlanCode,
  type ProgramCaps,
} from '@naya/shared'
import { BroadcastCadenceFields } from './BroadcastCadenceFields.js'
import { BroadcastCapabilityFields } from './BroadcastCapabilityFields.js'
import { ProgramCapsFields } from './ProgramCapsFields.js'
import { Drawer } from './Drawer.js'
import { adminApi, AdminApiError } from '../adminApi.js'
import {
  usePatchTenant,
  useReassignPlanTier,
} from '../hooks/useTenantMutations.js'
import { fmtDate } from '../format.js'
import type { TenantRow } from '../types.js'

const PLAN_LABEL: Record<PlanCode, string> = {
  starter: 'Starter',
  pro: 'Pro',
}

interface TenantEditDrawerProps {
  tenant: TenantRow
  onClose: () => void
}

/**
 * Tenant edit slide-over. Name / notes / billing cycle go through
 * PATCH /tenants/:id. Plan tier is separate — it's a scheduled reassignment
 * (effective next billing period), so it has its own control and its own
 * endpoint. Slug is intentionally absent: immutable post-creation. Logo is
 * also absent — owners manage their own logo via the crop UI on /branding
 * (slice 4a-2); an admin who needs to change it impersonates the owner.
 */
export function TenantEditDrawer({ tenant, onClose }: TenantEditDrawerProps) {
  const patch = usePatchTenant(tenant.id)
  const reassign = useReassignPlanTier(tenant.id)

  const [name, setName] = useState(tenant.name)
  const [notes, setNotes] = useState(tenant.notes ?? '')
  const [billingCycle, setBillingCycle] = useState(tenant.billing_cycle)
  const [maxStaff, setMaxStaff] = useState<number>(tenant.max_staff)
  const [maxBranches, setMaxBranches] = useState<number>(tenant.max_branches)
  // Per-tenant program caps override (null = inherit the plan tier).
  const [programCaps, setProgramCaps] = useState<ProgramCaps | null>(
    tenant.program_caps,
  )
  const [perDay, setPerDay] = useState<number | null>(
    tenant.max_broadcasts_per_day,
  )
  const [perWeek, setPerWeek] = useState<number | null>(
    tenant.max_broadcasts_per_week,
  )
  // Per-tenant broadcast-capability override (null = inherit plan tier).
  const [presetsEnabled, setPresetsEnabled] = useState<
    BroadcastSegmentKey[] | null
  >((tenant.broadcast_presets_enabled as BroadcastSegmentKey[] | null) ?? null)
  const [filterBuilderEnabled, setFilterBuilderEnabled] = useState<
    boolean | null
  >(tenant.broadcast_filter_builder_enabled)
  const [planTier, setPlanTier] = useState<PlanCode>(
    tenant.pending_plan_tier ?? tenant.plan_tier,
  )
  // SLICE-REFERRAL — superadmin entitlement toggle.
  const [referralEntitled, setReferralEntitled] = useState<boolean>(
    tenant.referral_entitled,
  )
  // SLICE-SECRET-MENU — superadmin entitlement toggle.
  const [secretMenuEntitled, setSecretMenuEntitled] = useState<boolean>(
    tenant.secret_menu_entitled,
  )
  const [backfilling, setBackfilling] = useState(false)

  async function runBackfill() {
    setBackfilling(true)
    try {
      const res = await adminApi.post<{ minted: number }>(
        `/admin/tenants/${tenant.id}/referral-backfill`,
      )
      toast.success(
        res.minted > 0
          ? `Minted ${res.minted} referral code${res.minted === 1 ? '' : 's'}.`
          : 'All eligible cards already have referral codes.',
      )
    } catch (err) {
      toast.error(
        err instanceof AdminApiError
          ? err.userMessage
          : 'Could not backfill referral codes.',
      )
    } finally {
      setBackfilling(false)
    }
  }

  function saveDetails() {
    patch.mutate(
      {
        name: name.trim(),
        notes: notes.trim() === '' ? null : notes.trim(),
        billing_cycle: billingCycle,
        max_staff: maxStaff,
        max_branches: maxBranches,
        program_caps: programCaps,
        max_broadcasts_per_day: perDay,
        max_broadcasts_per_week: perWeek,
        broadcast_presets_enabled: presetsEnabled,
        broadcast_filter_builder_enabled: filterBuilderEnabled,
        referral_entitled: referralEntitled,
        secret_menu_entitled: secretMenuEntitled,
      },
      {
        onSuccess: () => {
          toast.success('Tenant updated.')
          onClose()
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not update tenant.',
          ),
      },
    )
  }

  function savePlanTier() {
    reassign.mutate(
      { plan_tier: planTier },
      {
        onSuccess: (res) => {
          if (res.tenant.pending_plan_tier) {
            toast.success(
              `Plan tier reassignment scheduled for ${fmtDate(
                res.tenant.pending_plan_tier_effective_at,
              )}.`,
            )
          } else {
            toast.success('Pending plan tier change cleared.')
          }
          // Close the drawer — the tenant detail refetches and the pending
          // note renders there. Leaving the drawer open would show stale
          // "Currently …" hint text from the original tenant prop.
          onClose()
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not reassign plan tier.',
          ),
      },
    )
  }

  return (
    <Drawer title="Edit tenant" onClose={onClose}>
      <div className="admin-field">
        <label className="admin-label" htmlFor="edit-name">
          Name
        </label>
        <input
          id="edit-name"
          className="admin-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="admin-field">
        <label className="admin-label">Slug</label>
        <input className="admin-input" value={tenant.slug} disabled />
        <p className="admin-hint">
          Slug is immutable — changing it would break wallet pass and
          enrollment URLs.
        </p>
      </div>

      <div className="admin-field">
        <label className="admin-label" htmlFor="edit-billing">
          Billing cycle
        </label>
        <select
          id="edit-billing"
          className="admin-select"
          value={billingCycle}
          onChange={(e) =>
            setBillingCycle(e.target.value as 'monthly' | 'annual')
          }
        >
          <option value="monthly">Monthly</option>
          <option value="annual">Annual</option>
        </select>
      </div>

      <div className="admin-field">
        <label className="admin-label" htmlFor="edit-max-staff">
          Staff seats
        </label>
        <input
          id="edit-max-staff"
          className="admin-input"
          type="number"
          min={0}
          max={100}
          value={maxStaff}
          onChange={(e) => setMaxStaff(Number(e.target.value))}
        />
        <p className="admin-hint">
          Number of staff the owner can add (owner is always extra, not
          counted). Lowering this below the current staff count is allowed —
          existing staff stay, but no new seats can be added until the count
          drops.
        </p>
      </div>

      <div className="admin-field">
        <label className="admin-label" htmlFor="edit-max-branches">
          Branches
        </label>
        <input
          id="edit-max-branches"
          className="admin-input"
          type="number"
          min={1}
          max={100}
          value={maxBranches}
          onChange={(e) => setMaxBranches(Number(e.target.value))}
        />
        <p className="admin-hint">
          Maximum number of branches the owner can create. Lowering this
          below the current branch count is allowed — existing branches
          stay, but no new ones can be added until the count drops.
        </p>
      </div>

      <div className="admin-field">
        <label className="admin-label">Pass types &amp; limits</label>
        <label className="admin-check-row">
          <input
            type="checkbox"
            checked={programCaps !== null}
            onChange={(e) =>
              e.target.checked
                ? setProgramCaps({
                    loyalty: 1,
                    membership: 0,
                    event_ticket: 0,
                  })
                : setProgramCaps(null)
            }
          />
          Override the plan tier for this tenant
        </label>
        {programCaps === null ? (
          <p className="admin-hint" style={{ marginTop: 4 }}>
            This tenant inherits its pass types &amp; limits from its plan tier.
          </p>
        ) : (
          <div style={{ marginLeft: 22, marginTop: 6 }}>
            <p className="admin-hint" style={{ marginTop: 0 }}>
              Max programs the owner can create, per type:
            </p>
            <ProgramCapsFields
              value={programCaps}
              onChange={setProgramCaps}
              idPrefix="edit-caps"
            />
          </div>
        )}
      </div>

      <BroadcastCadenceFields
        perDay={perDay}
        perWeek={perWeek}
        onPerDayChange={setPerDay}
        onPerWeekChange={setPerWeek}
      />

      <BroadcastCapabilityFields
        presetsEnabled={presetsEnabled}
        filterBuilderEnabled={filterBuilderEnabled}
        onChange={(presets, builder) => {
          setPresetsEnabled(presets)
          setFilterBuilderEnabled(builder)
        }}
      />

      <div className="admin-field">
        <label className="admin-label">Referrals</label>
        <label className="admin-check-row">
          <input
            type="checkbox"
            checked={referralEntitled}
            onChange={(e) => setReferralEntitled(e.target.checked)}
          />
          Referral entitled
        </label>
        <p className="admin-hint">
          Lets the owner run a referral program (configured in their builder /
          Referrals page). Off by default. After enabling a live tenant, run the
          backfill to mint referral codes for their existing cards.
        </p>
        {tenant.referral_entitled && (
          <button
            type="button"
            className="admin-btn"
            style={{ marginTop: 6 }}
            onClick={runBackfill}
            disabled={backfilling}
          >
            {backfilling ? 'Minting…' : 'Backfill referral codes'}
          </button>
        )}
      </div>

      <div className="admin-field">
        <label className="admin-label">Secret menu</label>
        <label className="admin-check-row">
          <input
            type="checkbox"
            checked={secretMenuEntitled}
            onChange={(e) => setSecretMenuEntitled(e.target.checked)}
          />
          Secret menu entitled
        </label>
        <p className="admin-hint">
          Lets the owner tag stamps issued off the standard menu ("secret
          menu"). Off by default.
        </p>
      </div>

      <div className="admin-field">
        <label className="admin-label" htmlFor="edit-notes">
          Notes
        </label>
        <textarea
          id="edit-notes"
          name="tenant-notes"
          autoComplete="off"
          className="admin-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={saveDetails}
          disabled={patch.isPending}
        >
          {patch.isPending ? 'Saving…' : 'Save details'}
        </button>
      </div>

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--admin-border)',
          margin: '24px 0',
        }}
      />

      <div className="admin-field">
        <label className="admin-label" htmlFor="edit-plan">
          Plan tier
        </label>
        <select
          id="edit-plan"
          className="admin-select"
          value={planTier}
          onChange={(e) => setPlanTier(e.target.value as PlanCode)}
        >
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {PLAN_LABEL[p]}
            </option>
          ))}
        </select>
        <p className="admin-hint">
          Currently <strong>{PLAN_LABEL[tenant.plan_tier]}</strong>.
          {tenant.pending_plan_tier ? (
            <>
              {' '}
              Pending change to{' '}
              <strong>{PLAN_LABEL[tenant.pending_plan_tier]}</strong> on{' '}
              {fmtDate(tenant.pending_plan_tier_effective_at)}.
            </>
          ) : (
            ' Reassignment takes effect at the start of the next billing period.'
          )}
        </p>
        {tenant.pending_plan_tier && (
          <div className="admin-pending-note">
            Selecting the current tier ({PLAN_LABEL[tenant.plan_tier]}) clears
            the pending change.
          </div>
        )}
      </div>

      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-btn"
          onClick={savePlanTier}
          disabled={reassign.isPending}
        >
          {reassign.isPending ? 'Scheduling…' : 'Schedule plan tier change'}
        </button>
      </div>
    </Drawer>
  )
}
